<template>
	<el-dialog
		:modelValue="open"
		@update:modelValue="$emit('update:open', $event)"
		:title="shouldPublishComplaint ? 'Beschwerde veröffentlichen' : 'Veröffentlichung zurückziehen'"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
		@close="handleClose"
	>
		<el-alert type="info" effect="dark" :closable="false" show-icon>
			<template #title v-if="shouldPublishComplaint">
				<p>
					<strong>Durch die Veröffentlichung wird die Beschwerde auf der Website sichtbar.</strong> Sie können die Veröffentlichung jederzeit
					rückgängig machen.
				</p>
				<p>
					Haben Sie alle persönlichen Informationen aus der Beschreibung entfernt? Aus Datenschutzgründen müssen vor der Veröffentlichung alle
					persönlichen Informationen zu Beschwerdeführern und sonstigen Personen entfernt werden.
				</p>
			</template>
			<template #title v-else>
				<p>
					Durch das Zurückziehen der Veröffentlichung wird die Beschwerde von der Website entfernt. Sie können sie jederzeit wieder veröffentlichen.
				</p>
			</template>
		</el-alert>

		<TheForm
			:model="request"
			:rules="rules"
			:waiting="waiting"
			:waitingMsg="texts.formErrors.waiting"
			:showErr="showErr"
			:errMsg="errMsg"
			ref="formRef"
			:id="id"
			v-if="shouldPublishComplaint"
		>
			<el-form-item label="Bestätigung" prop="confirmation">
				<el-checkbox v-model="request.confirmation">Ja, ich habe alle persönlichen Informationen entfernt.</el-checkbox>
			</el-form-item>
		</TheForm>

		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="handlePublishComplaint" :disabled="waiting" :loading="waiting">
					{{ shouldPublishComplaint ? 'Veröffentlichen' : 'Veröffentlichung zurückziehen' }}
				</TheButton>
				<TheButton type="default" @buttonclick="handleClose">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { publishComplaint, unpublishComplaint } from '@/requests/complaints';
import { dialogBeforeClose, resetForm, validateForm, validateCheckbox } from '@/utils/functions';
import type { FormInstance } from 'element-plus';
import { defineComponent, ref } from 'vue';
import { texts } from '../../utils/texts';
import TheButton from '../TheButton.vue';
import TheForm from '../TheForm.vue';

interface PublishComplaintRequest {
	request: {
		confirmation: boolean;
	};
	rules: Object;
	id: string;
	waiting: boolean;
	showErr: boolean;
	errMsg: string;
}

export default defineComponent({
	name: 'DialogPublishComplaint',
	data(): PublishComplaintRequest {
		return {
			request: {
				confirmation: false,
			},
			rules: {
				confirmation: [{ validator: validateCheckbox, trigger: 'change' }],
			},
			id: 'publishComplaintForm',
			waiting: false,
			showErr: false,
			errMsg: texts.complaints.publishFailed,
		};
	},
	components: {
		TheButton,
		TheForm,
	},
	props: {
		open: {
			type: Boolean,
			required: true,
		},
		shouldPublishComplaint: {
			type: Boolean,
			default: true,
			required: true,
		},
		complaintId: {
			type: String,
			required: true,
		},
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
	},
	emits: ['reload', 'update:open'],
	methods: {
		async handlePublishComplaint() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid && this.shouldPublishComplaint) return;

			this.waiting = true;

			if (this.shouldPublishComplaint) {
				const publishResult = await publishComplaint(this.complaintId);
				if (publishResult.success) {
					this.handleClose(true);
				} else {
					this.showErr = true;
					this.errMsg = texts.complaints.publishFailed;
				}
			} else {
				const unpublishResult = await unpublishComplaint(this.complaintId);
				if (unpublishResult.success) {
					this.handleClose(true);
				} else {
					this.showErr = true;
					this.errMsg = texts.complaints.publishFailed;
				}
			}
			this.waiting = false;
		},
		handleClose(reload: Boolean) {
			resetForm(this.formRef, this.id);

			this.showErr = false;
			if (reload) {
				this.$emit('reload');
			}
			this.$emit('update:open', false);
		},
		dialogBeforeClose,
	},
	mounted() {},
});
</script>

<style lang="scss" scoped>
.el-form {
	margin-top: 2rem;
}
</style>
